import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Crosshair, TestTube, Wrench, Hammer, ShieldCheck, ClipboardCheck, Check } from "lucide-react";
import { SERVICES, IMG } from "@/lib/site";
import SEO, { buildServicesJsonLd } from "@/components/SEO";

const ICON_MAP = {
  diagnostics: Crosshair,
  testing: TestTube,
  corrosion: Wrench,
  spall: Hammer,
  cathodic: ShieldCheck,
  assessment: ClipboardCheck,
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Services"
        description="Concrete repair services across the UK: structural diagnostics, chemical testing (chlorides, carbonation, sulphates, ASR), reinforcement corrosion repair, spall repair, cathodic protection and post-fire assessment."
        jsonLd={buildServicesJsonLd(SERVICES)}
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// Services · Concrete Insight UK</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            From first survey to<br />final finish.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            Every service below is available as a stand-alone commission or as
            part of a full remediation package, spanning reinforcement
            corrosion, chemical attack (chlorides, carbonation, ASR,
            sulphates) and post-fire assessment. Priced per element, per m² or
            per programme.
          </p>
        </div>
      </section>

      {/* Services grid, asymmetric */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-zinc-200">
          {SERVICES.map((s, i) => {
            const Icon = ICON_MAP[s.id] || Wrench;
            const isWide = i === 0 || i === 3;
            return (
              <article
                key={s.id}
                className={`bg-white p-8 md:p-10 relative group ${isWide ? "lg:col-span-7" : "lg:col-span-5"} ${i % 2 === 0 ? "" : ""}`}
                data-testid={`service-detail-${s.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-mono text-xs text-zinc-500 tracking-[0.2em]">// SERVICE {s.code}</div>
                  <Icon size={22} className="text-zinc-500 group-hover:text-[#C2410C] transition-colors" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold uppercase mt-6 leading-[0.95]">
                  {s.title}
                </h2>
                <div className="text-[13px] text-zinc-500 mt-2">{s.tagline}</div>
                <div className="rule-line my-6 bg-zinc-200" />
                <p className="text-zinc-700 leading-relaxed">{s.body}</p>

                <ul className="mt-6 space-y-3">
                  {s.bullets.map((b, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-zinc-800">
                      <Check size={16} className="mt-1 text-[#C2410C] shrink-0" strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      {/* Method banner with real photo */}
      <section className="relative border-y border-zinc-900 bg-zinc-950">
        <div className="absolute inset-0">
          <img src={IMG.surveyMarking} alt="Investigation marking on beam" loading="lazy" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/90 to-zinc-950/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="tech-label text-[#D94515]">// Method statement</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] text-zinc-50 max-w-3xl">
            Every repair is executed to EN 1504.
          </h2>
          <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            The European standard for concrete repair isn&apos;t a suggestion, it&apos;s
            the yardstick a defensible repair is measured against. We work to
            it as a minimum on every project.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary bg-[#D94515] border-[#D94515] hover:bg-white hover:text-zinc-900" data-testid="services-cta-enquire">
              Discuss your project <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="tech-label">// Sectors we work with</div>
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
          Where our team gets called in.
        </h2>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-zinc-200">
          {[
            "Car Parks",
            "Bridges & Viaducts",
            "Balconies & Façades",
            "Basements & Substructure",
            "Industrial Slabs",
            "Marine Structures",
            "Water Retaining",
            "Historic Concrete",
          ].map((sector) => (
            <div key={sector} className="border-r border-b border-zinc-200 p-6 md:p-8">
              <div className="font-display text-xl md:text-2xl font-bold uppercase leading-tight">{sector}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
